<script>
import { useEsignStore } from '../../stores/esign'
import EcSpinner from '../ui/EcSpinner.vue'
import EcButton from '../ui/EcButton.vue'
import EcAlert from '../ui/EcAlert.vue'
import EcBadge from '../ui/EcBadge.vue'
import EcCollapse from '../ui/EcCollapse.vue'
import EcDrawer from '../ui/EcDrawer.vue'
import PackageEditor from './PackageEditor.vue'

export default {
  name: 'TemplateDetail',
  components: {
    EcSpinner,
    EcButton,
    EcAlert,
    EcBadge,
    EcCollapse,
    EcDrawer,
    PackageEditor,
  },
  props: {
    holderType: {
      type: String,
      required: true,
    },
    holderId: {
      type: [String, Number],
      required: true,
    },
    template: {
      type: Object,
      required: true,
    },
  },
  data() {
    var store = useEsignStore()
    return {
      store: store,
      model: null,
      loading: true,
      fieldsNames: [],
      showFieldsDrawer: false,
      fieldSearch: '',
      showDetails: false,
    }
  },
  computed: {
    t() {
      return this.$ecTranslate
    },
    editionUrl() {
      return (this.model && this.model.edition_url) || null
    },
    filteredFields() {
      var q = this.fieldSearch.trim().toLowerCase()
      if (!q) return this.fieldsNames
      return this.fieldsNames.filter(function (f) {
        return f.label.toLowerCase().includes(q) || f.code.toLowerCase().includes(q)
      })
    },
    roleLabels() {
      if (!this.model || !this.model.roles || !this.model.roles.length) return []
      return this.model.roles.map(function (r) { return typeof r === 'string' ? r : r.name })
    },
  },
  mounted() {
    this.loadDefaultData()
    this.loadTemplate()
  },
  methods: {
    formatDate(date) {
      if (!date) return '-'
      return new Date(date).toLocaleString()
    },
    async loadTemplate() {
      this.loading = true
      try {
        var results = await this.store.listTemplates(this.holderType, this.holderId, {
          Id: this.template.id,
          filter: { include_edition_url: 1 },
        })
        var self = this
        if (Array.isArray(results)) {
          this.model =
            results.find(function (t) { return String(t.id || t.Id) === String(self.template.id) }) || results[0] || null
        } else if (results && typeof results === 'object' && results.id) {
          this.model = results
        } else {
          this.model = this.template
        }
      } catch (e) {
        this.model = this.template
      } finally {
        this.loading = false
        if (this.model && this.model.edition_url) {
          this.$emit('request-fullscreen')
        }
      }
    },
    async loadDefaultData() {
      try {
        var data = await this.store.fetchDefaultData({})
        if (data && data.fields && data.fields.length) {
          this.fieldsNames = data.fields
        }
      } catch (e) {
        // Silent fail
      }
    },
    async copyToClipboard(text) {
      try {
        await navigator.clipboard.writeText(text)
        this.$ecNotify('Code "' + text + '" copié !', 'success')
        this.showFieldsDrawer = false
      } catch (e) {
        this.$ecNotify('Impossible de copier', 'error')
      }
    },
    async onOpenEditor() {
      var url = await this.store.openTemplateEditor(this.holderType, this.holderId, this.template.id)
      if (url) {
        window.open(url, '_blank')
      }
    },
  },
}
</script>

<template>
  <div class="ec-template-detail">
    <EcSpinner v-if="loading" />

    <template v-else-if="model">
      <!-- Compact header: title + code + actions on one line -->
      <div class="ec-template-detail__header">
        <div class="ec-template-detail__title-row">
          <h5 class="ec-template-detail__title">{{ model.name }}</h5>
          <span v-if="model.code" class="ec-template-detail__code">{{ model.code }}</span>
        </div>
        <div class="ec-template-detail__actions">
          <EcButton variant="outline-primary" size="sm" icon="pencil" @click="$emit('edit', model)">
            {{ t('ELIXIO_CONNECT.EDIT') }}
          </EcButton>
          <EcButton variant="outline-danger" size="sm" icon="trash" @click="$emit('delete', model)">
            {{ t('ELIXIO_CONNECT.DELETE') }}
          </EcButton>
        </div>
      </div>

      <!-- Collapsible details -->
      <EcCollapse :value="showDetails" @input="showDetails = $event">
        <template #trigger>
          <span class="ec-template-detail__collapse-trigger">
            <i class="bi bi-info-circle" />
            {{ t('ELIXIO_CONNECT.DETAILS') }}
          </span>
        </template>

        <div class="ec-template-detail__details">
          <!-- Description -->
          <div v-if="model.excerpt || model.description" class="ec-detail-section">
            <div class="ec-detail-section__label">
              <i class="bi bi-text-left" />
              {{ t('ELIXIO_CONNECT.EXCERPT') }}
            </div>
            <p class="ec-detail-section__text">{{ model.excerpt || model.description }}</p>
          </div>

          <!-- Roles -->
          <div v-if="roleLabels.length" class="ec-detail-section">
            <div class="ec-detail-section__label">
              <i class="bi bi-people" />
              {{ t('ELIXIO_CONNECT.ROLES') }}
            </div>
            <div class="ec-flex ec-gap-1" style="flex-wrap: wrap">
              <EcBadge v-for="(role, idx) in roleLabels" :key="idx" variant="primary">
                {{ role }}
              </EcBadge>
            </div>
          </div>

          <!-- Email message -->
          <div v-if="model.email_message" class="ec-detail-section">
            <div class="ec-detail-section__label">
              <i class="bi bi-envelope" />
              {{ t('ELIXIO_CONNECT.EMAIL_MESSAGE') }}
            </div>
            <p class="ec-detail-section__text">{{ model.email_message }}</p>
          </div>

          <!-- Settings row: language + timezone -->
          <div
            v-if="model.meta && (model.meta.language || model.meta.timezone)"
            class="ec-detail-row"
          >
            <div v-if="model.meta.language" class="ec-detail-chip">
              <i class="bi bi-translate" />
              {{ model.meta.language }}
            </div>
            <div v-if="model.meta.timezone" class="ec-detail-chip">
              <i class="bi bi-clock" />
              {{ model.meta.timezone }}
            </div>
          </div>

          <!-- Slug -->
          <div v-if="model.slug" class="ec-detail-section">
            <div class="ec-detail-section__label">
              <i class="bi bi-link-45deg" />
              Slug
            </div>
            <span class="ec-detail-section__mono">{{ model.slug }}</span>
          </div>

          <!-- Dates -->
          <div class="ec-detail-dates">
            <span>
              <i class="bi bi-calendar-plus" />
              {{ t('ELIXIO_CONNECT.CREATED_AT') }}: {{ formatDate(model.created_at) }}
            </span>
            <span>
              <i class="bi bi-calendar-check" />
              {{ t('ELIXIO_CONNECT.UPDATED_AT') }}: {{ formatDate(model.updated_at) }}
            </span>
          </div>
        </div>
      </EcCollapse>

      <!-- Editor area -->
      <div v-if="editionUrl" class="ec-template-detail__editor">
        <div v-if="fieldsNames.length" class="ec-flex ec-justify-end">
          <EcButton variant="link" size="sm" icon="code-slash" @click="showFieldsDrawer = true">
            {{ t('ELIXIO_CONNECT.SHOW_TEMPLATES_FIELDS_CODES') }}
          </EcButton>
        </div>
        <PackageEditor :editor-url="editionUrl" height="100%" />
      </div>

      <!-- No edition_url fallback -->
      <div v-else class="ec-template-detail__no-editor">
        <i class="bi bi-window-stack" style="font-size: 2rem; opacity: 0.4" />
        <p class="ec-text-muted" style="margin: 0">
          {{ t('ELIXIO_CONNECT.EDITOR') }}
        </p>
        <EcButton variant="primary" size="sm" icon="box-arrow-up-right" @click="onOpenEditor">
          Ouvrir dans une nouvelle fenêtre
        </EcButton>
      </div>
    </template>

    <EcAlert v-else type="warning">
      Modèle introuvable.
    </EcAlert>

    <!-- Field codes drawer -->
    <EcDrawer
      :value="showFieldsDrawer"
      :title="t('ELIXIO_CONNECT.FIELDS_CODES')"
      width="400px"
      @input="showFieldsDrawer = $event"
    >
      <div class="ec-mb-3">
        <div class="ec-input ec-input--has-icon">
          <i class="bi bi-search ec-input__icon" />
          <input
            v-model="fieldSearch"
            type="search"
            class="ec-input__field"
            :placeholder="t('ELIXIO_CONNECT.SEARCH_FIELD')"
          />
        </div>
      </div>

      <div class="ec-fields-list">
        <div
          v-for="field in filteredFields"
          :key="field.code"
          class="ec-fields-list__item"
        >
          <div class="ec-fields-list__info">
            <div class="ec-fields-list__label">{{ field.label }}</div>
            <div class="ec-fields-list__code">{{ field.code }}</div>
          </div>
          <EcButton
            variant="outline-secondary"
            size="sm"
            icon="clipboard"
            @click="copyToClipboard(field.code)"
          >
            {{ t('ELIXIO_CONNECT.COPY') }}
          </EcButton>
        </div>

        <div v-if="!filteredFields.length" class="ec-text-center ec-text-muted" style="padding: 1rem 0">
          {{ t('ELIXIO_CONNECT.NO_FIELD_FOUND') }}
        </div>
      </div>
    </EcDrawer>
  </div>
</template>

<style scoped>
.ec-template-detail {
  display: flex;
  flex-direction: column;
  height: 100%;
  gap: 0.5rem;
}

/* Header */
.ec-template-detail__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
}

.ec-template-detail__title-row {
  display: flex;
  align-items: baseline;
  gap: 0.5rem;
  min-width: 0;
}

.ec-template-detail__title {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: var(--ec-text-color, #1f2937);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.ec-template-detail__code {
  font-family: monospace;
  font-size: 0.75rem;
  color: var(--ec-text-muted, #6b7280);
  background: var(--ec-bg-muted, #f1f5f9);
  padding: 0.125rem 0.5rem;
  border-radius: 4px;
  white-space: nowrap;
}

.ec-template-detail__actions {
  display: flex;
  gap: 0.375rem;
  flex-shrink: 0;
}

/* Collapse trigger */
.ec-template-detail__collapse-trigger {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 0.8125rem;
  font-weight: 500;
  color: var(--ec-text-muted, #6b7280);
}

/* Details panel */
.ec-template-detail__details {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
}

.ec-detail-section {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.ec-detail-section__label {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--ec-text-muted, #6b7280);
  text-transform: uppercase;
  letter-spacing: 0.025em;
}

.ec-detail-section__text {
  margin: 0;
  font-size: 0.8125rem;
  color: var(--ec-text-color, #1f2937);
  line-height: 1.5;
}

.ec-detail-section__mono {
  font-family: monospace;
  font-size: 0.8125rem;
  color: var(--ec-text-color, #1f2937);
  background: var(--ec-bg-muted, #f1f5f9);
  padding: 0.125rem 0.5rem;
  border-radius: 4px;
  width: fit-content;
}

/* Language / timezone chips */
.ec-detail-row {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.ec-detail-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 0.8125rem;
  color: var(--ec-text-color, #1f2937);
  background: var(--ec-bg-muted, #f1f5f9);
  padding: 0.25rem 0.625rem;
  border-radius: 6px;
  border: 1px solid var(--ec-border-color, #e2e8f0);
}

.ec-detail-chip i {
  font-size: 0.75rem;
  color: var(--ec-text-muted, #6b7280);
}

/* Dates */
.ec-detail-dates {
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
  font-size: 0.75rem;
  color: var(--ec-text-muted, #9ca3af);
  padding-top: 0.25rem;
  border-top: 1px solid var(--ec-border-color, #f1f5f9);
}

.ec-detail-dates i {
  margin-right: 0.25rem;
}

/* Editor */
.ec-template-detail__editor {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

/* No editor fallback */
.ec-template-detail__no-editor {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  padding: 2rem 0;
  border: 2px dashed var(--ec-border-color, #e2e8f0);
  border-radius: 8px;
}

/* Field codes drawer */
.ec-fields-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.ec-fields-list__item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: var(--ec-bg-muted, #f8f9fa);
  border: 1px solid var(--ec-border-color, #e2e8f0);
  border-radius: 8px;
  padding: 0.5rem 0.75rem;
  transition: background 0.15s ease;
}

.ec-fields-list__item:hover {
  background: rgba(59, 130, 246, 0.04);
}

.ec-fields-list__info {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.ec-fields-list__label {
  font-weight: 600;
  font-size: 0.875rem;
  color: var(--ec-text-color, #1f2937);
}

.ec-fields-list__code {
  font-family: monospace;
  color: var(--ec-text-muted, #6b7280);
  font-size: 0.8125rem;
}
</style>
